import {
    Component,
    Input,
    Inject,
    OnInit,
    HostListener,
    OnDestroy
} from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { Subscription, combineLatest } from 'rxjs';
import { where } from 'underscore';

import { MediaTypeModel } from '../../models/media-type.model';
import { MediaSubscriptionService } from '../../../Core/services/media-subscription.service';
import { MediaTypeEnum, getSocialMediaPlatformTypes } from '@Common/Basic/enums/media-type.enum';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { MediaSubscription } from '@Monitor/Shared/models/media-subscription.model';
import { ActionKeys } from '../../../../Common/Forms/enums/keys.enum';
import { ControlValueAccessorModel, getProvider } from '../../../../Common/Forms/models/control-value-accessor.model';
import { mediaTypeToMediaSubTypeMap, MediaSubscriptionMediaTypeEnum } from '@api/mediaSubscriptions/enums/media-subscription-media-type.enum';
import { BroadcastSubTypeEnum } from '../../../../Common/Basic/enums/broadcast-sub-type.enum';
import { FilterOptionModel } from '../../../../Common/Filter/models/filter-option.model';

/**
* ### Accessibility Usage
* + ***ariaDescribedBy*** - use this if the text field has an external error message. Use message's id
* + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
*/
@Component({
    selector: 'agpr-media-type-checkbox',
    templateUrl: './media-type-checkbox.component.html',
    styleUrls: ['./media-type-checkbox.component.scss'],
    providers: [getProvider(MediaTypeCheckboxComponent)],
    standalone: false
})
export class MediaTypeCheckboxComponent extends ControlValueAccessorModel<MediaTypeModel[]> implements OnInit, OnDestroy {

    /**
     * Connects the keyword groups to the RHP, and disables all media types if any keyword groups include
     * image search.
     */
    @Input() set isImageSearch(isImageSearch: boolean) {
        this.shouldDisableMedia = isImageSearch;
        if (this.shouldDisableMedia) {
            this.mediaTypeIds = [MediaTypeEnum.ONLINE_NEWS];

            if (this.availableMediaTypes.length > 0) {
                this.onCheckboxUpdate();
            }
        }
    }
    @Input() ariaDescribedBy: string;
    @Input() set saveSearch(savedSearch: boolean) {
        this.isSavedSearch = savedSearch;
        this.calculateMediaTypes(this.availableMediaTypes, this.isSavedSearch);
    }

    // all social media types except tiktok
    socialMediaTypes: MediaTypeModel[] = [];
    savedSearchSocialMediaTypes: MediaTypeModel[] = [];
    magazineMediaType: MediaTypeModel;
    otherMediaType: MediaTypeModel;

    broadcastMediaTypes: MediaTypeModel[] = [];
    isTvChecked: boolean = false;
    tvSubTypes: MediaTypeModel[] = [];
    tvSubTypeIds: string[] = [];

    otherMediaTypes: MediaTypeModel[] = [];

    // contains the user selected media types
    value: MediaTypeModel[] = [];
    // contains the user selected media ids
    mediaTypeIds: string[] = [];

    // media types allowed based on account config
    availableMediaTypes: MediaTypeEnum[] = [];

    enableSelectAll: boolean = false;
    shouldDisableMedia: boolean = false;
    isSavedSearch: boolean = false;
    savedSearchMediaTypesCollapsed: boolean = true;

    hasTvEyes: boolean = false;
    hasSocial: boolean = false;
    hasSubscriptions: boolean = false;

    private mediaSubscriptions: MediaSubscription[] = [];
    private subscriptions: Subscription[] = [];

    readonly buttonTypes = ButtonTypesEnum;
    readonly mediaTypes = MediaTypeEnum;
    readonly broadcastSubTypes = BroadcastSubTypeEnum;

    /** True when a checked TV type has no sub type selected. */
    hasBroadcastSubTypeError: boolean = false;

    readonly mediaTypesColumnOrder = [
        MediaTypeEnum.ONLINE_NEWS,
        MediaTypeEnum.BLOGS,
        MediaTypeEnum.PRINT,
        MediaTypeEnum.PRESS_RELEASE,
        MediaTypeEnum.RADIO,
        MediaTypeEnum.PODCAST,
        MediaTypeEnum.TV,
        MediaTypeEnum.FACEBOOK,
        MediaTypeEnum.INSTAGRAM,
        MediaTypeEnum.TWITTER,
        MediaTypeEnum.BLUESKY,
        MediaTypeEnum.YOUTUBE,
        MediaTypeEnum.REDDIT,
        MediaTypeEnum.TIKTOK,
        MediaTypeEnum.SNAPCHAT,
        MediaTypeEnum.THREADS,
        MediaTypeEnum.LINKEDIN,
        MediaTypeEnum.WECHAT,
        MediaTypeEnum.SINA_WEIBO,
        MediaTypeEnum.TWITCH,
        MediaTypeEnum.PINTEREST,
        MediaTypeEnum.TUMBLR,
        MediaTypeEnum.TELEGRAM,
        MediaTypeEnum.WHATSAPP,
        MediaTypeEnum.VK,
        MediaTypeEnum.MAGAZINE,
        MediaTypeEnum.OTHER,
    ];

    constructor(
        private monitorConfigService: MonitorConfigService,
        private mediaSubscriptionService: MediaSubscriptionService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
    ) {
        super();
    }

    /**
     * Sets available MediaTypeOptions & checks selected boxes
     */
    ngOnInit() {
        this.subscriptions.push(
            combineLatest([
                this.monitorConfigService.hasSocialConfigAccess(),
                this.mediaSubscriptionService.all$,
                this.monitorConfigService.getAvailableMediaTypes(),
                this.monitorConfigService.getAvailableSocialMediaTypes(),
            ]).subscribe(([hasSocial, savedSubs, mediaTypes, availableSocialMediaTypes]) => {
                this.hasSocial = hasSocial;
                this.mediaSubscriptions = savedSubs;
                this.availableMediaTypes = Array.from(new Set([...mediaTypes, ...availableSocialMediaTypes]));

                this.calculateMediaTypes(this.availableMediaTypes, this.isSavedSearch);
            }),
            this.monitorConfigService.getTvEyesEnabled().subscribe(
                hasTvEyes => this.hasTvEyes = hasTvEyes
            ),
            this.monitorConfigService.hasSubscriptions().subscribe(
                hasSubs => this.hasSubscriptions = hasSubs
            )
        );
    }


    writeValue(value: MediaTypeModel[]) {
        this.value = value;
        this.mediaTypeIds = this.getSelectedMediaTypeIds(this.value);

        const tvMediaType = this.value.find(mediaType => mediaType.id === MediaTypeEnum.TV);
        this.isTvChecked = !!tvMediaType;
        this.tvSubTypeIds = tvMediaType?.children?.map(option => option.value.childId) ?? [];

        // If in image search mode, override to only ONLINE_NEWS
        if (this.shouldDisableMedia) {
            this.mediaTypeIds = [MediaTypeEnum.ONLINE_NEWS];
            const onlineNewsModel = this.getMediaTypeModelById(MediaTypeEnum.ONLINE_NEWS);
            this.value = onlineNewsModel ? [onlineNewsModel] : [];
        }

        this.savedSearchMediaTypesCollapsed = !this.value.some(type => type.isSavedSearch);
        this.hasBroadcastSubTypeError = MediaTypeModel.hasMissingBroadcastSubType(this.value);

        this.onModelChange(this.value);
    }


    /**
     * Populate mediaType arrays based on param settings
     * @param allowedMediaTypes - provisioned media types will be enabled, otherwise disabled
     * @param isSavedSearch - determines what media types will be added to the display arrays
     */
    private calculateMediaTypes(allowedMediaTypes: MediaTypeEnum[], isSavedSearch: boolean = false) {
        if (!allowedMediaTypes || allowedMediaTypes.length === 0) {

            return;
        }

        this.enableSelectAll = false;
        this.broadcastMediaTypes = [];
        this.tvSubTypes = [];
        this.socialMediaTypes = [];
        this.otherMediaTypes = [];
        this.savedSearchSocialMediaTypes = [];

        this.mediaTypesColumnOrder
            .map((mediaType) => new MediaTypeModel(mediaType))
            .forEach(mediaType => {
                if (!mediaType.isSavedSearch && !isSavedSearch) {
                    mediaType.isProvisioned = allowedMediaTypes.includes(mediaType.id);
                } else {
                    mediaType.isProvisioned = true;
                }

                if (mediaType.id === MediaTypeEnum.TV ||
                    mediaType.id === MediaTypeEnum.RADIO ||
                    mediaType.id === MediaTypeEnum.PODCAST
                ) {
                    // @TODO: For TV and radio, we need to disable if
                    // 1. account only has tv eyes and not cm
                    // 2. there are no existing tv eyes sub
                    // it is currently impossible to know this with currently available backend endpoints
                    this.broadcastMediaTypes.push(mediaType);
                    if (mediaType.id === MediaTypeEnum.TV) {
                        this.tvSubTypes = MediaTypeModel.getTvSubTypes();
                    }
                }

                if (mediaType.isSocial) {
                    if (isSavedSearch || !isSavedSearch && !mediaType.isSavedSearch) {
                        // social media options will be disabled if there are no subscriptions for the given media type in the account,
                        // unless Social Listening is enabled or media options are saved search specific
                        if (!this.hasSocial && !mediaType.isSavedSearch) {
                            if (this.mediaSubscriptions.length > 0) {
                                mediaType.isDisabled = !this.hasAnySubscriptionsOfType(mediaTypeToMediaSubTypeMap.get(mediaType.id));
                            } else {
                                mediaType.isDisabled = true;
                            }
                        }
                        if (isSavedSearch && mediaType.isSavedSearch) {
                            if (mediaType.id === MediaTypeEnum.OTHER) {
                                this.otherMediaType = mediaType;
                            } else {
                                this.savedSearchSocialMediaTypes.push(mediaType);
                            }
                        } else {
                            this.socialMediaTypes.push(mediaType);
                        }

                    }
                }

                if (mediaType.id === MediaTypeEnum.MAGAZINE) {
                    this.magazineMediaType = mediaType;
                }

                if (mediaType.id === MediaTypeEnum.BLOGS ||
                    mediaType.id === MediaTypeEnum.ONLINE_NEWS ||
                    mediaType.id === MediaTypeEnum.PRESS_RELEASE ||
                    mediaType.id === MediaTypeEnum.PRINT
                ) {
                    this.otherMediaTypes.push(mediaType);
                }

                // If there's at least one non-social media type, display the select all / select none button.
                if (!mediaType.isSocial && mediaType.isProvisioned) {
                    this.enableSelectAll = true;
                }

                if (mediaType.isDisabled) {
                    const index = this.mediaTypeIds.indexOf(mediaType.id);
                    if (index >= 0) {
                        this.mediaTypeIds.splice(index, 1);
                        this.onCheckboxUpdate();
                    }
                }
            });
    }


    /**
     * Unselects all available options
     */
    unselectAll() {
        this.mediaTypeIds = [];
        this.onCheckboxUpdate();
    }

    /**
     * Selects all traditional media type available options (excluding social)
     */
    selectAll() {
        const socialMediaPlatformTypes = getSocialMediaPlatformTypes();
        if (this.isSavedSearch) {
            this.mediaTypeIds = this.mediaTypesColumnOrder.filter(media => !socialMediaPlatformTypes.includes(media)).slice();
        } else {
            this.mediaTypeIds = this.availableMediaTypes.filter(media => !socialMediaPlatformTypes.includes(media)).slice();
        }
        // "Select All" checks every TV sub type.
        this.tvSubTypeIds = this.tvSubTypes.map(subType => subType.childId);
        this.onCheckboxUpdate();
    }

    /**
     * Updates the selected media type object by iterating over an array of
     * strings and adding objects with matching id's from the available MediaTypes.
     */
    onCheckboxUpdate() {
        this.value = [];

        // A fresh manual check defaults to "Programs" only.
        const wasTvChecked = this.isTvChecked;
        this.isTvChecked = this.mediaTypeIds.includes(MediaTypeEnum.TV);
        if (!wasTvChecked && this.isTvChecked && this.tvSubTypeIds.length === 0) {
            this.tvSubTypeIds = [this.broadcastSubTypes.PROGRAM];
        }

        for (const mediaTypeId of this.mediaTypeIds) {
            const mediaTypeModel = this.getMediaTypeModelById(mediaTypeId);
            if (mediaTypeModel === null) {
                continue;
            } else {
                this.value.push(mediaTypeModel);
            }
        }
        this.writeValue(this.value);
    }

    /**
     * Finds and returns a media type model from the lists of available media types using the mediaTypes' id.
     *
     * @param mediaTypeId The id representing the media type model to be found
     *
     * @returns MediaTypeModel - The media type model found by its id
     */
    getMediaTypeModelById(mediaTypeId: string): MediaTypeModel {
        for (const mediaTypeModel of this.otherMediaTypes) {
            if (mediaTypeModel.id === mediaTypeId) {
                return mediaTypeModel;
            }
        }
        for (const mediaTypeModel of this.socialMediaTypes) {
            if (mediaTypeModel.id === mediaTypeId) {
                return mediaTypeModel;
            }
        }
        for (const mediaTypeModel of this.broadcastMediaTypes) {
            if (mediaTypeModel.id === mediaTypeId) {
                if (mediaTypeModel.id === MediaTypeEnum.TV) {
                    mediaTypeModel.children = this.tvSubTypes
                        .filter(subType => this.tvSubTypeIds.includes(subType.childId))
                        .map(subType => ({
                            label: subType.name,
                            value: subType
                        } as FilterOptionModel));
                }
                return mediaTypeModel;
            }
        }
        for (const mediaTypeModel of this.savedSearchSocialMediaTypes) {
            if (mediaTypeModel.id === mediaTypeId) {
                return mediaTypeModel;
            }
        }
        if (mediaTypeId === this.magazineMediaType.id) {
            return this.magazineMediaType;
        } else if (mediaTypeId === this.otherMediaType.id) {
            return this.otherMediaType;
        }


        return null;
    }

    /**
     * Returns an array of media type id's from a MediaTypeModel
    */
    getSelectedMediaTypeIds(data: MediaTypeModel[]): string[] {
        return [...data.map(mediaType => mediaType.id)];
    }

    goToManageSubscriptions(event?: any) {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }

        window.open(this.appBaseHref + 'Monitoring/subscriptions', '_blank');
    }

    goToManageSocialTopics(event?: any) {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }

        window.open(this.appBaseHref + 'Social/themes', '_blank');
    }

    /** Checks if there are any subscriptions of the given type in the account. */
    private hasAnySubscriptionsOfType(type: MediaSubscriptionMediaTypeEnum): boolean {
        const matchingSubscriptions: MediaSubscription[] = where(this.mediaSubscriptions, {
            type: type
        }) || [];


        return matchingSubscriptions.length > 0;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
